import React, { useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { ThemeProvider } from './contexts/ThemeContext';
import SplashScreen from './components/SplashScreen';
import HRChatbot from './components/HRChatbot';
import Login from './pages/Login';
import Dashboard from './pages/Dashboard';
import Employees from './pages/Employees';
import Attendance from './pages/Attendance';
import Leave from './pages/Leave';
import Payroll from './pages/Payroll';
import Incentives from './pages/Incentives';
import KPI from './pages/KPI';
import Reports from './pages/Reports';
import UserManagement from './pages/UserManagement';
import Documents from './pages/Documents';
import './App.css';

const PrivateRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();
  
  // Also check localStorage directly as a fallback
  const hasToken = localStorage.getItem('token');
  
  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-[hsl(220_6%_6%)] dark:bg-[hsl(220_6%_6%)] light:bg-white">
        <div className="loading-spinner"></div>
      </div>
    );
  }
  
  return (isAuthenticated || hasToken) ? children : <Navigate to="/login" />;
};

const AppRoutes = () => {
  const { isAuthenticated } = useAuth();
  
  return (
    <>
      <Routes>
        <Route path="/login" element={<Login />} />
        <Route path="/" element={<PrivateRoute><Dashboard /></PrivateRoute>} />
        <Route path="/employees" element={<PrivateRoute><Employees /></PrivateRoute>} />
        <Route path="/attendance" element={<PrivateRoute><Attendance /></PrivateRoute>} />
        <Route path="/leave" element={<PrivateRoute><Leave /></PrivateRoute>} />
        <Route path="/payroll" element={<PrivateRoute><Payroll /></PrivateRoute>} />
        <Route path="/incentives" element={<PrivateRoute><Incentives /></PrivateRoute>} />
        <Route path="/kpi" element={<PrivateRoute><KPI /></PrivateRoute>} />
        <Route path="/reports" element={<PrivateRoute><Reports /></PrivateRoute>} />
        <Route path="/documents" element={<PrivateRoute><Documents /></PrivateRoute>} />
        <Route path="/users" element={<PrivateRoute><UserManagement /></PrivateRoute>} />
      </Routes>
      {isAuthenticated && <HRChatbot />}
    </>
  );
};

function App() {
  const [showSplash, setShowSplash] = useState(true);
  const [splashComplete, setSplashComplete] = useState(false);

  // Check if splash was shown recently (within last 30 minutes)
  useEffect(() => {
    const lastSplash = localStorage.getItem('lastSplashTime');
    const now = Date.now();
    
    // Skip splash if shown within last 30 minutes (for better UX during development)
    if (lastSplash && (now - parseInt(lastSplash)) < 30 * 60 * 1000) {
      setShowSplash(false);
      setSplashComplete(true);
    }
  }, []);

  const handleSplashComplete = () => {
    localStorage.setItem('lastSplashTime', Date.now().toString());
    setSplashComplete(true);
    // Small delay before hiding splash for smooth transition
    setTimeout(() => setShowSplash(false), 100);
  };

  return (
    <ThemeProvider>
      <AuthProvider>
        {showSplash && <SplashScreen onComplete={handleSplashComplete} />}
        {splashComplete && (
          <BrowserRouter>
            <AppRoutes />
          </BrowserRouter>
        )}
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
